import React, { useState } from 'react';
import { Shield, Sun, Wheat, Mountain, Factory, AlertTriangle, Building2, ChevronRight, Check } from 'lucide-react';
import { services } from '../data/mock';
import { Button } from './ui/button';

const iconMap = {
  Shield,
  Sun,
  Wheat,
  Mountain,
  Factory,
  AlertTriangle,
  Building2
};

const ServicesSection = () => {
  const [activeService, setActiveService] = useState(services[0]);

  const IconComponent = iconMap[activeService.icon];

  return (
    <section id="services" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-sm tracking-[0.3em] text-gray-500 uppercase mb-3">What We Do</p>
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-gray-900 mb-6">
            Our Core Services
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#002060] to-gray-400 mx-auto mb-6" />
          <p className="max-w-2xl mx-auto text-gray-600">
            Comprehensive solutions across critical sectors, delivered with expertise and precision
          </p>
        </div>

        {/* Services Navigation */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {services.map((service) => {
            const Icon = iconMap[service.icon];
            return (
              <button
                key={service.id}
                onClick={() => setActiveService(service)}
                className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeService.id === service.id
                    ? 'bg-[#002060] text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <Icon size={18} />
                <span className="hidden sm:inline">{service.title}</span>
              </button>
            );
          })};
        </div>

        {/* Active Service Detail */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="grid lg:grid-cols-2">
            {/* Left Content */}
            <div className="p-8 lg:p-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-[#002060] rounded-xl flex items-center justify-center">
                  <IconComponent className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl md:text-3xl font-serif font-medium text-gray-900">
                  {activeService.title}
                </h3>
              </div>
              
              <p className="text-gray-600 leading-relaxed mb-6">
                {activeService.fullDesc}
              </p>

              {activeService.infrastructure && (
                <p className="text-gray-600 leading-relaxed mb-8 p-4 bg-slate-50 rounded-lg border-l-4 border-[#002060]">
                  {activeService.infrastructure}
                </p>
              )}

              <Button
                onClick={() => {
                  const element = document.querySelector('#contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-[#002060] hover:bg-[#001040] text-white px-6 py-3 font-medium group"
              >
                Discuss Your Needs
                <ChevronRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            {/* Right Content - Capabilities */}
            <div className="bg-slate-50 p-8 lg:p-12 overflow-y-auto max-h-[600px]">
              <h4 className="text-lg font-semibold text-gray-900 mb-6">Our Capabilities</h4>
              <div className="space-y-4">
                {activeService.capabilities.map((capability, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-4 bg-white rounded-lg hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="w-6 h-6 bg-[#002060]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-[#002060]" />
                    </div>
                    <span className="text-gray-700">{capability}</span>
                  </div>
                ))}
              </div>

              {/* Development Types for Commercial Development */}
              {activeService.developmentTypes && (
                <div className="mt-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Development Types</h4>
                  <div className="space-y-3">
                    {activeService.developmentTypes.map((type, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100"
                      >
                        <div className="w-2 h-2 bg-[#002060] rounded-full flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Services Grid Preview */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mt-12">
          {services.map((service) => {
            const Icon = iconMap[service.icon];
            const isActive = activeService.id === service.id;
            return (
              <div
                key={service.id}
                onClick={() => setActiveService(service)}
                className={`cursor-pointer p-5 rounded-xl text-center transition-all duration-300 ${
                  isActive
                    ? 'bg-[#002060] text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:shadow-lg hover:-translate-y-1'
                }`}
              >
                <Icon className={`w-7 h-7 mx-auto mb-2 ${isActive ? 'text-white' : 'text-[#002060]'}`} />
                <p className="text-xs font-medium leading-tight">{service.title}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
